import { BffUnauthenticatedError, bffFetch } from "@/shared/api/client";
import { mapOptimizeResponse, type RawV2OptimizeResponse } from "@/domain/quant/frontier-map";
import type { OptimizeBasis } from "@/domain/quant/optimize-request";
import type { RawAnalysis } from "@/domain/quant/portfolio-development";
import {
  buildAnalysisRequestBody,
  idFromLocation,
  quantTaskRaceLost,
} from "@/domain/quant/suggested-portfolio";
import { QuantBffError, type OptimizeResponse, type QuantBffStep } from "@/domain/quant/types";

const ANALYSIS_ATTEMPTS = 3;
const encodePathSegment = encodeURIComponent;
const sleep = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds));

export async function lastOptimize(portfolioId: string): Promise<OptimizeResponse | null> {
  const response = await send(
    `v1/quant-portfolio/${encodePathSegment(portfolioId)}/last-optimize`,
    {},
    "last-optimize",
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new QuantBffError(
      "last-optimize",
      `BFF last-optimize failed: ${response.status} ${response.statusText}`,
      response.status,
    );
  }
  let raw: RawV2OptimizeResponse;
  try {
    raw = (await response.json()) as RawV2OptimizeResponse;
  } catch {
    throw new QuantBffError("last-optimize", "BFF last-optimize returned an unparseable body");
  }
  const optimizeResponse = mapOptimizeResponse(raw);
  if (optimizeResponse.prospect_portfolios.length === 0) {
    throw new QuantBffError(
      "last-optimize",
      "BFF last-optimize returned a stored set with an empty frontier",
    );
  }
  return optimizeResponse;
}

export async function currentPortfolioAnalysis(
  portfolioId: string,
  startValue: number,
  basis: OptimizeBasis,
  signal?: AbortSignal,
): Promise<RawAnalysis> {
  const body = buildAnalysisRequestBody(startValue, basis);
  const encodedPortfolioId = encodePathSegment(portfolioId);
  const id = await createAnalysis(
    `v1/quant-portfolio/${encodedPortfolioId}/analysis`,
    body,
    signal,
  );
  const analysis = await readAnalysis(
    `v1/quant-portfolio/${encodedPortfolioId}/analysis/${id}`,
    signal,
  );
  return { ...analysis, id };
}

export async function prospectPortfolioAnalysis(
  prospectSetId: string | number,
  prospectPortfolioId: string | number,
  startValue: number,
  basis: OptimizeBasis,
  signal?: AbortSignal,
): Promise<RawAnalysis> {
  const body = buildAnalysisRequestBody(startValue, basis);
  const encodedProspectSetId = encodePathSegment(String(prospectSetId));
  const encodedProspectPortfolioId = encodePathSegment(String(prospectPortfolioId));
  const base = `v1/quant-portfolio/prospect-portfolio-set/${encodedProspectSetId}/prospect-portfolio/${encodedProspectPortfolioId}/analysis`;
  const id = await createAnalysis(base, body, signal);
  const analysis = await readAnalysis(`${base}/${id}`, signal);
  return { ...analysis, id };
}

async function send(
  path: string,
  init: { method?: string; json?: unknown; signal?: AbortSignal },
  step: QuantBffStep,
): Promise<Response> {
  try {
    return await bffFetch(path, init);
  } catch (err) {
    if (err instanceof BffUnauthenticatedError || isAbortError(err)) throw err;
    throw new QuantBffError(
      step,
      `BFF ${step} request failed: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { name?: unknown }).name === "AbortError"
  );
}

async function detailOf(response: Response): Promise<string> {
  try {
    const text = (await response.text()).trim();
    return text ? ` — ${text}` : "";
  } catch {
    return "";
  }
}

async function createAnalysis(path: string, body: unknown, signal?: AbortSignal): Promise<string> {
  let last: QuantBffError | null = null;
  for (let attempt = 1; attempt <= ANALYSIS_ATTEMPTS; attempt++) {
    signal?.throwIfAborted();
    const response = await send(path, { method: "POST", json: body, signal }, "analysis");
    if (response.ok) {
      return createdId(response, "create portfolio analysis", "analysis");
    }
    const detail = await detailOf(response);
    last = new QuantBffError(
      "analysis",
      `BFF create analysis failed: ${response.status} ${response.statusText}${detail}`,
      response.status,
    );
    if (!quantTaskRaceLost(response.status, detail) || attempt === ANALYSIS_ATTEMPTS) throw last;
    await sleep(attempt * 1000);
  }
  throw last as QuantBffError;
}

async function readAnalysis(path: string, signal?: AbortSignal): Promise<RawAnalysis> {
  const response = await send(path, { signal }, "analysis");
  if (!response.ok) {
    throw new QuantBffError(
      "analysis",
      `BFF analysis read failed: ${response.status} ${response.statusText}`,
      response.status,
    );
  }
  try {
    return (await response.json()) as RawAnalysis;
  } catch (err) {
    if (isAbortError(err)) throw err;
    throw new QuantBffError("analysis", "BFF analysis read returned an unparseable body");
  }
}

function createdId(response: Response, operation: string, step: QuantBffStep): string {
  const id = idFromLocation(response.headers.get("location"));
  if (!id) {
    throw new QuantBffError(
      step,
      `${operation}: no id in the Location header (the resource was created; its id cannot be resolved)`,
    );
  }
  return id;
}
